import { useCallback, useEffect, useState } from 'react'
import { LayoutGrid, X, Loader2, SquarePen, Trash2, Building2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { usePlatformFilterStore } from '../../../store/platformFilterStore'
import { platformPropertiesService } from '../../../services/platformPropertiesService'
import type { Property, PropertyType, CurrencyCode } from '../../../types/api.types'
import { CURRENCY_OPTIONS } from '../../admin/constants/adminConstants'
import { LoadErrorState } from '../../../components/LoadErrorState'
import { EmptyState } from '../../admin/components/EmptyState'
import { Tooltip } from '../../../components/Tooltip'

const PROPERTY_TYPES: PropertyType[] = ['HOTEL', 'RESTAURANT', 'SECURITY']

interface PropForm { name: string; code: string; propertyType: PropertyType; currency: CurrencyCode }
const EMPTY: PropForm = { name: '', code: '', propertyType: 'HOTEL', currency: 'PKR' }

export function PlatformPropertiesPage(): JSX.Element {
  const { organizations, selectedOrgId, selectOrg } = usePlatformFilterStore()

  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [filterType, setFilterType] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<PropForm>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [nameErr, setNameErr] = useState('')
  const [codeErr, setCodeErr] = useState('')

  const loadData = useCallback(async (type = filterType) => {
    if (!selectedOrgId) { setProperties([]); return }
    setLoading(true); setLoadError(false)
    try { setProperties(await platformPropertiesService.getByOrg(selectedOrgId, type || undefined)) }
    catch { setLoadError(true) }
    finally { setLoading(false) }
   
  }, [selectedOrgId, filterType])

  useEffect(() => { loadData() }, [loadData])

  async function handleSubmit() {
    let ok = true
    if (!form.name.trim()) { setNameErr('Required'); ok = false }
    if (!form.code.trim()) { setCodeErr('Required'); ok = false }
    if (!ok || !selectedOrgId) return
    setSubmitting(true)
    try {
      const payload = { name: form.name.trim(), code: form.code.trim().toUpperCase(), property_type: form.propertyType, currency: form.currency, organization_id: selectedOrgId }
      if (editingId) {
        await platformPropertiesService.update(selectedOrgId, editingId, {
          name: payload.name,
          code: payload.code,
          property_type: payload.property_type,
          currency: payload.currency,
        })
        toast.success('Property updated')
      } else {
        await platformPropertiesService.create(selectedOrgId, payload)
        toast.success('Property created')
      }
      setShowModal(false); setEditingId(null); setForm(EMPTY)
      await loadData()
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Save failed')
    } finally { setSubmitting(false) }
  }

  async function handleDelete(prop: Property) {
    if (!selectedOrgId || !window.confirm(`Delete property "${prop.name}"? This cannot be undone.`)) return
    try {
      await platformPropertiesService.delete(selectedOrgId, prop.id)
      toast.success('Property deleted')
      await loadData()
    } catch { toast.error('Delete failed') }
  }

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId)

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Properties</h1>
        <p className="mt-1 text-sm text-gray-500">Select an organisation to manage its properties.</p>
      </div>

      {!selectedOrgId ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-20 text-center">
          <Building2 size={32} className="mb-3 text-gray-300" />
          <p className="text-[14px] font-medium text-gray-500">No organisation selected</p>
          <p className="mt-1 text-[13px] text-gray-400">Use the Organisation dropdown in the top bar.</p>
          {organizations.length > 0 && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {organizations.slice(0, 5).map((o) => (
                <button key={o.id} type="button" onClick={() => selectOrg(o.id)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] text-gray-600 hover:border-brand-green hover:text-brand-green">
                  {o.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[13px] text-gray-500">
              Properties for <span className="font-medium text-gray-900">{selectedOrg?.name ?? `Org #${selectedOrgId}`}</span>
            </p>
            <div className="flex items-center gap-2">
              <select value={filterType}
                onChange={(e) => { setFilterType(e.target.value); loadData(e.target.value) }}
                className="h-8 rounded-lg border border-gray-200 bg-white px-3 text-[13px] text-gray-700 outline-none focus:border-brand-green">
                <option value="">All Types</option>
                {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); setShowModal(true) }}
                className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black hover:opacity-70">
                + Create Property
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={22} className="animate-spin text-brand-green" />
              <span className="ml-2 text-sm text-gray-500">Loading properties…</span>
            </div>
          ) : loadError ? (
            <LoadErrorState message="Couldn't load properties" onRetry={loadData} />
          ) : properties.length === 0 ? (
            <EmptyState icon={LayoutGrid} message="No properties in this organisation" />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Name</th>
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Code</th>
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Type</th>
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Currency</th>
                    <th className="px-5 py-3 text-center text-[12px] font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50/40">
                      <td className="px-5 py-3.5 text-[13px] font-medium text-gray-900">{p.name}</td>
                      <td className="px-5 py-3.5 font-mono text-[13px] text-gray-600">{p.code}</td>
                      <td className="px-5 py-3.5 text-[13px] text-gray-600">{p.property_type}</td>
                      <td className="px-5 py-3.5 text-[13px] text-gray-600">{p.currency}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-3">
                          <Tooltip label="Edit property">
                            <button type="button" onClick={() => {
                              setEditingId(p.id)
                              setForm({ name: p.name, code: p.code, propertyType: p.property_type, currency: p.currency as CurrencyCode })
                              setShowModal(true)
                            }} className="text-brand-green hover:text-brand-green/70"><SquarePen size={15} /></button>
                          </Tooltip>
                          <Tooltip label="Delete property">
                            <button type="button" onClick={() => handleDelete(p)} className="text-red-400 hover:text-red-600">
                              <Trash2 size={15} />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-[15px] font-bold text-gray-900">{editingId ? 'Edit Property' : 'Create Property'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Property Name*</label>
                <input value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setNameErr('') }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] outline-none focus:border-brand-green ${nameErr ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="e.g. Main Branch" />
                {nameErr && <p className="mt-1 text-[12px] text-red-500">{nameErr}</p>}
              </div>
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Property Code*</label>
                <input value={form.code} onChange={(e) => { setForm({ ...form, code: e.target.value.toUpperCase() }); setCodeErr('') }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] font-mono outline-none focus:border-brand-green ${codeErr ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="e.g. GH-01" />
                {codeErr && <p className="mt-1 text-[12px] text-red-500">{codeErr}</p>}
              </div>
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Property Type</label>
                <select value={form.propertyType} onChange={(e) => setForm({ ...form, propertyType: e.target.value as PropertyType })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-green">
                  {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Currency</label>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value as CurrencyCode })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-green">
                  {CURRENCY_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={handleSubmit} disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black hover:opacity-90 disabled:opacity-50">
                {submitting ? 'Saving…' : editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowModal(false)}
                className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
